import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateCountryFactMutation,
  useGetCountryFactQuery,
  useUpdateCountryFactMutation
} from '../../../../../services/endpoints/content/countryFacts';
import { CountryFact } from '../../../../../types/content.type';
import { FadeIn } from '../../../../animations/FadeIn';
import { CountryFactForm } from './CountryFactsForm';

const steps = [{ name: 'Details', form: CountryFactForm }];

export interface FormValues {
  id?: number;
  name: string;
  content: string;
  image?: File;
  image_source: string;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
  countryFact?: CountryFact;
}

const countryFactLinksMap = (id?: number | string) => {
  return {
    edit: `/admin/content/country-facts/${id}/edit`,
    detail: `/admin/content/country-facts/${id}`,
    list: `/admin/content/country-facts`
  };
};

interface AddEditCountryFactProps {
  editing?: boolean;
}

export const AddEditCountryFacts: React.FC<AddEditCountryFactProps> = ({ editing }) => {
  const params = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createCountryFact] = useCreateCountryFactMutation();
  const [updateCountryFact] = useUpdateCountryFactMutation();

  const {
    data: countryFactData,
    error: countryFactError,
    isLoading: countryFactLoading
  } = useGetCountryFactQuery(Number(params.id!), { skip: !editing || !params.id });

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    content: '',
    image: {} as File,
    image_source: ''
  });

  const validationSchemas = [
    Yup.object({
      name: Yup.string().required('Name is required'),
      content: Yup.string().required('content is required')
    })
  ];

  useEffect(() => {
    if (editing && countryFactError && !countryFactLoading) {
      addToast('Error while loading admin', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, countryFactError, countryFactLoading]);

  useEffect(() => {
    if (countryFactData?.countryFact)
      setInitialValues({
        id: countryFactData?.countryFact.id,
        name: countryFactData?.countryFact.name,
        content: countryFactData?.countryFact?.content ?? '',
        image_source: countryFactData?.countryFact.image_source
      });
  }, [countryFactData]);

  const handleAddCountryFact = async (formValue: FormValues) => {
    try {
      if (editing) {
        const res = await updateCountryFact({
          ...formValue,
          id: Number(params.id)!
        }).unwrap();
        if (res && res.countryFact) {
          navigate(countryFactLinksMap(res.countryFact.id!)?.list);
          addToast(`Country Fact updated successfully`, 'success');
        }
      } else {
        const res = await createCountryFact(formValue).unwrap();
        if (res && res.countryFact) {
          navigate(countryFactLinksMap(res.countryFact.id!)?.list);
          addToast('Country Fact created successfully', 'success');
        } else throw new Error('A problem happened while creating Country Fact');
      }
    } catch (e) {
      if (e.data && e.data.error) {
        addToast(e.data.error[0], 'error');
      }
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        id: true,
        name: true,
        content: true
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text="Back"
            color="#2B4E64"
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Country Facts
          </h2>
          {steps.length > 1 && (
            <Stepper
              editing
              steps={steps}
              currentStep={currentStep}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleAddCountryFact}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue,
                      ...(countryFactData && {
                        countryFact: countryFactData.countryFact
                      })
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
